import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { HelpStateService } from './help-state.service';
import { TourService } from './tour.service';

const MARGIN = 12;
const SPOT_PAD = 6;
const MAX_BUBBLE_WIDTH = 360;

interface BubblePosition {
  top: number;
  left: number;
  width: number;
}

const LABELS = {
  es: { step: 'Paso {n} de {total}', prev: 'Anterior', next: 'Siguiente', done: 'Listo', exit: 'Salir', guide: 'Recorrido guiado' },
  it: { step: 'Passo {n} di {total}', prev: 'Indietro', next: 'Avanti', done: 'Fatto', exit: 'Esci', guide: 'Percorso guidato' },
};

@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [A11yModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (step(); as s) {
      @if (s.rect) {
        <div
          class="tour-spot"
          aria-hidden="true"
          [style.top.px]="s.rect.top - pad"
          [style.left.px]="s.rect.left - pad"
          [style.width.px]="s.rect.width + pad * 2"
          [style.height.px]="s.rect.height + pad * 2"></div>
      } @else {
        <div class="tour-dim" aria-hidden="true"></div>
      }
      <div
        #bubble
        class="tour-bubble"
        role="dialog"
        aria-modal="false"
        [attr.aria-label]="labels().guide"
        aria-describedby="tour-bubble-text"
        cdkTrapFocus
        [style.top.px]="pos().top"
        [style.left.px]="pos().left"
        [style.width.px]="pos().width">
        @if (!s.blocked) {
          <p class="tour-bubble__progress">{{ progressLabel() }}</p>
        }
        <h2 class="tour-bubble__title">{{ s.title[lang()] }}</h2>
        <p id="tour-bubble-text" class="tour-bubble__text">{{ s.text[lang()] }}</p>
        <div class="tour-bubble__actions">
          @if (!s.blocked) {
            <button type="button" class="tour-btn tour-btn--ghost" (click)="tour.pause()">{{ labels().exit }}</button>
          }
          <span class="tour-bubble__spacer"></span>
          @if (!s.isFirst && !s.blocked) {
            <button type="button" class="tour-btn tour-btn--secondary" (click)="tour.prev()">{{ labels().prev }}</button>
          }
          @if (s.fix; as fix) {
            <button type="button" class="tour-btn tour-btn--secondary" (click)="tour.next()">{{ labels().done }}</button>
            <button #nextBtn type="button" class="tour-btn tour-btn--primary" (click)="tour.applyFix()">{{ fix.label }}</button>
          } @else {
            <button #nextBtn type="button" class="tour-btn tour-btn--primary" (click)="tour.next()">
              {{ s.isLast ? labels().done : labels().next }}
            </button>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 30000;
      pointer-events: none;
    }
    .tour-spot {
      position: fixed;
      border-radius: 10px;
      box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55);
      outline: 3px solid #38bdf8;
      transition: top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease;
      pointer-events: none;
    }
    .tour-dim {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.55);
      pointer-events: none;
    }
    .tour-bubble {
      position: fixed;
      max-width: calc(100vw - 24px);
      background: #ffffff;
      color: #0f172a;
      border-radius: 14px;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.3);
      padding: 1rem 1.1rem 0.9rem;
      pointer-events: auto;
      transition: top 0.2s ease, left 0.2s ease;
    }
    .tour-bubble__progress {
      margin: 0 0 0.25rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: #0089ac;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .tour-bubble__title {
      margin: 0 0 0.35rem;
      font-size: 1rem;
      font-weight: 700;
    }
    .tour-bubble__text {
      margin: 0 0 0.9rem;
      font-size: 0.9rem;
      line-height: 1.45;
      color: #334155;
    }
    .tour-bubble__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .tour-bubble__spacer { flex: 1; }
    .tour-btn {
      border: 1px solid transparent;
      border-radius: 8px;
      padding: 0.45rem 0.9rem;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      min-height: 36px;
    }
    .tour-btn:focus-visible { outline: 2px solid #0089ac; outline-offset: 2px; }
    .tour-btn--primary { background: #0089ac; color: #fff; }
    .tour-btn--primary:hover { background: #006d8a; }
    .tour-btn--secondary { background: #e6f4f8; color: #006d8a; }
    .tour-btn--ghost { background: transparent; color: #64748b; padding-inline: 0.4rem; }
    .tour-btn--ghost:hover { color: #0f172a; }
    @media (prefers-reduced-motion: reduce) {
      .tour-spot, .tour-bubble { transition: none; }
    }
  `],
})
export class TourOverlayComponent {
  readonly tour = inject(TourService);
  private state = inject(HelpStateService);

  readonly pad = SPOT_PAD;
  readonly step = this.tour.active;
  readonly lang = this.state.lang;
  readonly labels = computed(() => LABELS[this.lang()]);
  readonly progressLabel = computed(() => {
    const s = this.step();
    if (!s) return '';
    return this.labels().step.replace('{n}', String(s.index + 1)).replace('{total}', String(s.total));
  });

  private bubble = viewChild<ElementRef<HTMLElement>>('bubble');
  private nextBtn = viewChild<ElementRef<HTMLButtonElement>>('nextBtn');
  private viewport = signal({ width: window.innerWidth, height: window.innerHeight });
  private bubbleHeight = signal(160);
  readonly pos = computed(() => this.computePosition());

  private lastFocusedKey = '';

  constructor() {
    // El layout vive dentro de `.content.z-10` (contexto de apilamiento propio): sin moverlo a <body>,
    // los p-dialog con appendTo="body" quedan por encima del recorrido.
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    inject(DOCUMENT).body.appendChild(host);
    inject(DestroyRef).onDestroy(() => host.remove());

    effect(() => {
      const s = this.step();
      const el = this.bubble()?.nativeElement;
      if (!s || !el) {
        this.lastFocusedKey = '';
        return;
      }
      this.lang();
      requestAnimationFrame(() => {
        this.viewport.set({ width: window.innerWidth, height: window.innerHeight });
        this.bubbleHeight.set(el.offsetHeight || 160);
        const key = `${s.tour.id}:${s.index}:${s.blocked}`;
        if (key !== this.lastFocusedKey) {
          this.lastFocusedKey = key;
          this.nextBtn()?.nativeElement.focus({ preventScroll: true });
        }
      });
    });
  }

  private computePosition(): BubblePosition {
    const s = this.step();
    const { width: vw, height: vh } = this.viewport();
    const width = Math.min(MAX_BUBBLE_WIDTH, vw - MARGIN * 2);
    const height = this.bubbleHeight();
    const clampLeft = (left: number) => Math.min(Math.max(left, MARGIN), vw - width - MARGIN);
    const clampTop = (top: number) => Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - height - MARGIN));

    if (!s?.rect) {
      return { width, left: clampLeft((vw - width) / 2), top: clampTop((vh - height) / 2) };
    }

    const r = s.rect;
    const left = clampLeft(r.left + r.width / 2 - width / 2);
    const below = r.top + r.height + SPOT_PAD + MARGIN;
    const above = r.top - SPOT_PAD - MARGIN - height;
    let top: number;
    if (below + height <= vh - MARGIN) {
      top = below;
    } else if (above >= MARGIN) {
      top = above;
    } else {
      top = vh - height - MARGIN;
    }
    return { width, left, top: clampTop(top) };
  }
}
